import { useCallback } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { useUserQuery } from 'api/user/queries'
import { localStorageKeys } from 'consts/localStorageKeys'
import { getUuidStorageKey } from 'utils/getUuidStorageKey'

type AccordionNamespace = 'conversations_group' | 'details' | 'query_suggestions' | 'references' | 'thoughts'

type AccordionState = {
  accordions: Record<string, boolean | undefined>
  closeAccordion: (storageKey: string) => void
  openAccordion: (storageKey: string) => void
  toggleAccordion: ({ isOpenByDefault, storageKey }: { isOpenByDefault?: boolean, storageKey: string }) => void
}

const useAccordionsStateStore = create<AccordionState>()(
  persist(
    (set, get) => ({
      accordions: {},

      closeAccordion: storageKey =>
        set(state => ({
          accordions: { ...state.accordions, [storageKey]: false },
        })),

      openAccordion: storageKey =>
        set(state => ({
          accordions: { ...state.accordions, [storageKey]: true },
        })),

      toggleAccordion: ({ isOpenByDefault, storageKey }) => {
        const { accordions } = get()
        const storedValue = accordions[storageKey]
        const currentValue = storedValue ?? isOpenByDefault
        const updatedValue = !currentValue

        set({
          accordions: { ...accordions, [storageKey]: updatedValue },
        })
      },
    }),
    {
      name: localStorageKeys.accordions,
    },
  ),
)

export const useCloseAccordion = (namespace: AccordionNamespace) => {
  const closeAccordionStore = useAccordionsStateStore(state => state.closeAccordion)
  const { data: uuid } = useUserQuery({ select: ({ user }) => user.uuid })

  return useCallback((id: string) => {
    const storageKey = getUuidStorageKey({ storageKey: `${namespace}:${id}`, uuid })
    closeAccordionStore(storageKey)
  }, [closeAccordionStore, namespace, uuid])
}

export const useToggleAccordion = ({ isOpenByDefault = false, namespace }: { isOpenByDefault?: boolean, namespace: AccordionNamespace }) => {
  const toggleAccordionStore = useAccordionsStateStore(state => state.toggleAccordion)
  const { data: uuid } = useUserQuery({ select: ({ user }) => user.uuid })

  return useCallback((id: string) => {
    const storageKey = getUuidStorageKey({ storageKey: `${namespace}:${id}`, uuid })
    toggleAccordionStore({ isOpenByDefault, storageKey })
  }, [isOpenByDefault, namespace, toggleAccordionStore, uuid])
}

export const useIsAccordionOpen = ({ id, isOpenByDefault = false, namespace }: { id: string, isOpenByDefault?: boolean, namespace: AccordionNamespace }) => {
  const { data: uuid } = useUserQuery({ select: ({ user }) => user.uuid })

  const storageKey = getUuidStorageKey({ storageKey: `${namespace}:${id}`, uuid })
  const storedValue = useAccordionsStateStore(state => state.accordions[storageKey])
  const isAccordionOpen = storedValue ?? isOpenByDefault

  return isAccordionOpen
}
